import { dateOf, toInstant, type Instant, type LocalDateTime } from './time';
import { sum, type Rupees } from './money';
import { calc, span } from './pricing';
import { phase } from './availability';
import type { BookingRecord, Customer } from './types';

/** Last 10 digits of a phone number (drops +91 / 0 prefixes and punctuation). */
export function phoneKey(p: string): string {
  const d = p.replace(/\D/g, '');
  return d.length > 10 ? d.slice(-10) : d;
}

export function numbersOf(c: Customer): string[] {
  return [c.phone, c.whatsapp, c.alt].map(phoneKey).filter(x => x.length >= 6);
}

/** Name substring, or ≥ 3 digits matching inside any of the customer's numbers. */
export function findCustomers(customers: readonly Customer[], q: string): Customer[] {
  q = q.toLowerCase().trim();
  if (!q) return [];
  const digits = q.replace(/\D/g, '');
  return customers.filter(c => c.name.toLowerCase().includes(q) || (digits.length >= 3 && numbersOf(c).some(n => n.includes(digits))));
}

/**
 * Customers sharing a number with `phone`. Family numbers are shared on purpose, so this is only ever
 * shown as a note — people are never merged by phone alone.
 */
export function sharedNumber(customers: readonly Customer[], phone: string, excludeId?: string): Customer[] {
  const k = phoneKey(phone);
  if (k.length < 10) return [];
  return customers.filter(c => c.id !== excludeId && numbersOf(c).includes(k));
}

export interface CustomerStats {
  records: BookingRecord[]; bookings: number; completed: number; value: Rupees; outstanding: Rupees;
  last: LocalDateTime | null; open: number;
}

export function customerStats(records: readonly BookingRecord[], custId: string, now: Instant): CustomerStats {
  const rs = records.filter(r => r.cust === custId);
  const booked = rs.filter(r => r.status === 'confirmed' || r.status === 'completed');
  const visited = rs.filter(r => r.status === 'completed' || (r.status === 'confirmed' && toInstant(span(r).start) < now));
  const last = visited.map(r => span(r).end).sort().reverse()[0] ?? null;
  return {
    records: rs, bookings: booked.length, completed: rs.filter(r => r.status === 'completed').length,
    value: sum(booked.map(r => calc(r).total)),
    outstanding: sum(rs.filter(r => r.status === 'confirmed').map(r => Math.max(0, calc(r).balance))),
    last, open: rs.filter(r => ['enquiry', 'hold'].includes(phase(r, now))).length,
  };
}

export const firstName = (c: Pick<Customer, 'name'>) => c.name.split(' ')[0]!.replace(/&.*/, '').trim();
export const initials = (c: Pick<Customer, 'name'>) => c.name.replace(/&/g, ' ').split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0]!.toUpperCase()).join('');
export const sinceLabel = (c: Customer) => dateOf(c.since);
